import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { criarDenuncia, enviarAnexos, minhasAbordagens, ETAPAS, TIPOS, DESFECHOS, TIPOS_RETORNO } from "../services/api";
import { useAuth } from "../context/AuthContext";
import EmpresaAutocomplete from "./EmpresaAutocomplete";
import ShareButtons from "./ShareButtons";
import { AvisoEmail, useCampoEmail } from "./AvisoEmail";

// Espelha a whitelist do backend (validação amigável antes do envio).
const MAX_ANEXOS = 5;
const MAX_BYTES = 5 * 1024 * 1024;
const EXTS_OK = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".pdf"];
const RASCUNHO_KEY = "eairh_denuncia_rascunho"; // captura de intenção: guarda o relato do visitante deslogado p/ restaurar após o cadastro

function ComplaintForm({ embedded = false, initial = null, onClose } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const closeIfModal = () => { if (embedded && typeof onClose === "function") onClose(); };
  // Navegação a partir do modal: fecha o popup antes de mudar de rota (senão ele ficaria por cima da nova página).
  const irPara = (path) => { closeIfModal(); navigate(path); };

  const [form, setForm] = useState({
    natureza: 0, // 0=reclamação (default) · 1=elogio · 2=neutro
    empresa: "",
    empresaId: null,
    titulo: "",
    vaga: "",
    dataProcesso: "",
    etapa: 0,
    tipo: 0,
    prazoRespostaDias: 7,
    autorNome: "",
    descricao: "",
    dificuldade: "",
    desfecho: 0,
    perguntas: "",
    tipoRetorno: 0,
    contatoNome: "",
    contatoEmail: "",
    contatoWhatsapp: "",
    diasParaRetorno: "",
    anonimo: false,
    mostrarNomeParaEmpresa: true, // só a EMPRESA citada vê o nome (nunca o público) — para ela achar o processo
    cienteContato: false, // consentimento p/ enviar o convite ao contato informado
    intermediar: false,   // opt-in: candidato quer que o eaiRH convide a empresa (revela o card de contato)
    website: "", // honeypot (oculto): bots preenchem, humanos não
  });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(false);
  const [destino, setDestino] = useState(null); // empresaId p/ navegação manual quando o anexo falha
  const [relatoId, setRelatoId] = useState(null); // id da reclamação criada (p/ compartilhar o permalink /relato/:id)
  const [anexos, setAnexos] = useState([]);
  const [rascunhoRecuperado, setRascunhoRecuperado] = useState(false);
  const [abordagens, setAbordagens] = useState([]); // empresas que abordaram o candidato pela plataforma (pré-preenche)

  // Se o candidato foi abordado por alguma empresa pelo eaiRH, sugerimos pré-preencher (vínculo consensual → cobrança direta).
  useEffect(() => {
    if (!user || user.tipo !== 0) return;
    minhasAbordagens().then(setAbordagens).catch(() => {});
  }, [user]);

  // Captura de intenção (A): se o visitante preencheu e foi criar conta, recupera o relato ao voltar.
  useEffect(() => {
    try {
      const d = localStorage.getItem(RASCUNHO_KEY);
      if (d) { setForm((f) => ({ ...f, ...JSON.parse(d) })); setRascunhoRecuperado(true); }
    } catch { /* rascunho inválido: ignora */ }
  }, []);

  // Pré-preenche empresa/vaga quando vindo de um link (ex.: aviso de vaga encerrada por inatividade:
  // /denunciar?empresa=...&vaga=...). Só preenche campos ainda vazios — não sobrescreve rascunho recuperado.
  useEffect(() => {
    const empresa = searchParams.get("empresa");
    const vaga = searchParams.get("vaga");
    const tipo = searchParams.get("tipo"); // ex.: home → /denunciar?tipo=1 (pré-seleciona a categoria de desrespeito)
    if (!empresa && !vaga && tipo == null) return;
    setForm((f) => ({
      ...f,
      empresa: f.empresa || empresa || "",
      vaga: f.vaga || vaga || "",
      titulo: f.titulo || (vaga ? `Sem retorno na vaga "${vaga}"` : ""),
      tipo: tipo != null && /^[0-3]$/.test(tipo) ? Number(tipo) : f.tipo,
    }));
  }, [searchParams]);

  // Pré-preenche via PROP quando aberto como popup (modal não tem URL). Só preenche campos vazios.
  useEffect(() => {
    if (!initial) return;
    setForm((f) => ({
      ...f,
      empresa: f.empresa || initial.empresa || "",
      empresaId: f.empresaId || initial.empresaId || null,
      vaga: f.vaga || initial.vaga || "",
      titulo: f.titulo || (initial.vaga ? `Sem retorno na vaga "${initial.vaga}"` : ""),
      tipo: initial.tipo != null && /^[0-3]$/.test(String(initial.tipo)) ? Number(initial.tipo) : f.tipo,
    }));
  }, [initial]);

  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });
  // E o e-mail que dispara o convite a empresa: errado aqui, o convite nunca chega e o relato fica sem resposta.
  const campoEmail = useCampoEmail(form.contatoEmail, (v) => setForm((f) => ({ ...f, contatoEmail: v })));

  const onPickFiles = (e) => {
    const escolhidos = Array.from(e.target.files || []);
    setErro(null);
    if (escolhidos.length > MAX_ANEXOS) {
      setErro(`Máximo de ${MAX_ANEXOS} anexos.`);
      e.target.value = "";
      return;
    }
    for (const f of escolhidos) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!EXTS_OK.includes(ext)) {
        setErro(`"${f.name}": use JPG, PNG, WEBP, GIF ou PDF.`);
        e.target.value = "";
        return;
      }
      if (f.size > MAX_BYTES) {
        setErro(`"${f.name}" excede 5 MB.`);
        e.target.value = "";
        return;
      }
    }
    setAnexos(escolhidos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro(null);
    // Captura de intenção (A): visitante deslogado preenche tudo; ao publicar, salvamos o rascunho e levamos
    // ao cadastro explicando O PORQUÊ (a conta é o que deixa o eaiRH interagir/cobrar a empresa por ele).
    // A conta segue obrigatória pra publicar (âncora anti-spam/difamação) — só reordenamos a UX.
    if (!user) {
      try { localStorage.setItem(RASCUNHO_KEY, JSON.stringify(form)); } catch { /* quota/privado */ }
      closeIfModal(); // se aberto como popup, fecha antes de ir ao login (o rascunho volta preenchido depois)
      navigate("/entrar", { state: {
        from: "/denunciar",
        motivo: "Falta um passo: criar sua conta (10s, pode ser anônima). Ela protege sua identidade, guarda seu relato e te avisa se a empresa responder. Seu relato foi guardado e volta preenchido.",
      } });
      return;
    }
    // Consentimento: se informou e-mail do contato, precisa confirmar ciência do envio do convite.
    if (form.contatoEmail.trim() && !form.cienteContato) {
      setErro("Você informou o e-mail do recrutador. Marque que está ciente de que enviaremos um convite à empresa antes de publicar.");
      return;
    }
    setEnviando(true);
    try {
      const nova = await criarDenuncia({
        natureza: Number(form.natureza),
        empresa: form.empresa.trim(),
        empresaId: form.empresaId,
        titulo: form.titulo.trim(),
        descricao: form.descricao.trim(),
        vaga: form.vaga.trim() || null,
        etapa: Number(form.etapa),
        tipo: Number(form.tipo),
        dataProcesso: form.dataProcesso || null,
        prazoRespostaDias: Number(form.prazoRespostaDias) || 7,
        autorNome: form.anonimo ? "Anônimo" : (user ? user.nome : "Anônimo"),
        anonimo: form.anonimo,
        mostrarNomeParaEmpresa: form.mostrarNomeParaEmpresa,
        dificuldadeProcesso: form.dificuldade ? Number(form.dificuldade) : null,
        desfecho: Number(form.desfecho),
        perguntasEntrevista: form.perguntas.trim() || null,
        tipoRetorno: Number(form.tipoRetorno),
        diasParaRetorno: form.diasParaRetorno ? Number(form.diasParaRetorno) : null,
        contatoNome: form.contatoNome.trim() || null,
        contatoEmail: form.contatoEmail.trim() || null,
        contatoWhatsapp: form.contatoWhatsapp.trim() || null,
        cienteContato: form.cienteContato, // consentimento LGPD (prova gravada no backend)
        website: form.website, // honeypot
      });
      let anexoErro = null;
      if (anexos.length > 0) {
        try {
          await enviarAnexos(nova.id, anexos);
        } catch (errAnexo) {
          // A denúncia já foi criada; o anexo é complementar — registra o erro p/ EXIBIR na tela de sucesso.
          anexoErro = errAnexo.message;
        }
      }
      try { localStorage.removeItem(RASCUNHO_KEY); } catch { /* ok */ } // publicado: rascunho não é mais necessário
      setDestino(nova.empresaId);
      setRelatoId(nova.id);
      setOk(true); // trava o form (evita reenvio → denúncia duplicada), já que a denúncia foi criada
      if (anexoErro) {
        // Anexo falhou: a tela de sucesso mostra o aviso + botão para seguir manualmente e reanexar na empresa.
        setErro(`Relato publicado, mas houve falha ao anexar: ${anexoErro}. Você pode anexar depois na página da empresa.`);
      }
      // Sem auto-redirect: a tela de agradecimento (abaixo) reforça o valor e oferece os próximos passos por botão.
    } catch (e) {
      setErro(e.message || "Erro ao enviar o relato.");
    } finally {
      setEnviando(false);
    }
  };

  const labelCls = "block text-gray-700 text-sm font-bold mb-2";
  const inputCls = "shadow-sm appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary";
  const hojeISO = new Date().toISOString().slice(0, 10); // limite do seletor de data (backend rejeita data futura)

  if (ok) {
    // Caminho feliz: tela verde + redirecionamento. Se o anexo falhou, mostra o aviso (âmbar) com
    // navegação MANUAL — sem isto, a tela de sucesso esconderia o erro e o usuário não saberia que a evidência não subiu.
    if (erro) {
      return (
        <div role="alert" className="max-w-lg mx-auto p-6 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 text-center">
          <p>⚠️ {erro}</p>
          <button onClick={() => irPara(`/empresas/${destino}`)} className="mt-4 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2.5">
            Ir para a página da empresa
          </button>
        </div>
      );
    }
    return (
      <div role="status" className="max-w-lg mx-auto p-6 rounded-2xl border border-green-200 bg-green-50 text-center">
        <div className="text-4xl">✅</div>
        <h2 className="mt-2 text-xl font-bold text-green-800">Relato publicado! Obrigado por compartilhar. 🙏</h2>
        <p className="mt-2 text-sm text-gray-600">
          Seu relato já ajuda outros candidatos e incentiva a empresa a dar retorno. A empresa pode responder pelo portal — e <strong>avisamos você por e-mail</strong> se isso acontecer.
        </p>
        <div className="mt-5 rounded-xl border border-green-200 bg-white p-4">
          <p className="text-sm font-semibold text-gray-800">📣 Avise o próximo candidato</p>
          <p className="mt-0.5 text-xs text-gray-500">Compartilhe e ajude mais gente a saber com quem está lidando.</p>
          <ShareButtons
            url={relatoId ? `/relato/${relatoId}` : (destino ? `/empresas/${destino}` : "/")}
            text={`Relato sobre o processo seletivo da ${form.empresa || "empresa"} no eaiRH:`}
            className="mt-3 justify-center"
          />
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
          <button onClick={() => irPara(`/empresas/${destino}`)}
            className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2.5">
            Ver na página da empresa
          </button>
          <Link to="/minhas-reclamacoes" onClick={closeIfModal}
            className="rounded-xl border border-primary text-primary font-semibold px-5 py-2.5 hover:bg-primary hover:text-white transition">
            Acompanhar em "Meus relatos"
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
      {erro && (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 text-sm">{erro}</div>
      )}

      {/* 1º passo — natureza da experiência. Roteia o fluxo: só a RECLAMAÇÃO aciona direito de resposta/cobrança. */}
      <div className="mb-5">
        <label className="block text-sm font-bold text-gray-800">Como foi sua experiência com esse processo?</label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {[
            { v: 0, em: "😞", t: "Tive um problema", tag: "reclamação", cls: "text-red-700 bg-red-50" },
            { v: 1, em: "😊", t: "Fui bem tratado", tag: "elogio", cls: "text-green-700 bg-green-50" },
            { v: 2, em: "😐", t: "Só registrar", tag: "neutro", cls: "text-gray-600 bg-gray-100" },
          ].map((o) => {
            const sel = Number(form.natureza) === o.v;
            return (
              <button type="button" key={o.v} onClick={() => setForm((f) => ({ ...f, natureza: o.v }))} aria-pressed={sel}
                className={`text-left rounded-xl border p-3 transition ${sel ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200 bg-white hover:border-primary/40"}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xl" aria-hidden>{o.em}</span>
                  <span className="font-semibold text-sm text-gray-900">{o.t}</span>
                </div>
                <span className={`mt-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full ${o.cls}`}>{o.tag}</span>
              </button>
            );
          })}
        </div>
        {Number(form.natureza) === 1 && <p className="mt-1.5 text-xs text-green-700">💚 Elogios não acionam cobrança — a empresa é avisada para <b>agradecer</b>, e isso vira vitrine de marca empregadora.</p>}
        {Number(form.natureza) === 2 && <p className="mt-1.5 text-xs text-gray-500">Registro informativo — fica no histórico da empresa, sem acionar cobrança.</p>}
      </div>

      {!user && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-800 text-sm">
          ✍️ Pode preencher agora. No fim, criamos sua conta (10s, <b>pode ser anônima</b>) — ela <b>protege sua identidade</b> e te avisa se a empresa responder. Falar com a empresa é <b>opcional</b> (você escolhe no fim).
        </div>
      )}
      {user && rascunhoRecuperado && (
        <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800 text-sm">
          ✓ Recuperamos o relato que você começou. Confira e clique em <b>Publicar relato</b> para enviar.
        </div>
      )}

      {abordagens.length > 0 && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-900 font-semibold">Você foi abordado(a) por estas empresas aqui no eaiRH:</p>
          <p className="text-xs text-blue-700 mt-0.5 mb-2">É sobre o processo de alguma delas? Toque para já preencher (a empresa recebe a cobrança direto).</p>
          <div className="flex flex-wrap gap-1.5">
            {abordagens.map((a) => (
              <button type="button" key={a.empresaId} onClick={() => setForm((f) => ({ ...f, empresa: a.nome, empresaId: a.empresaId }))}
                className={`rounded-full px-3 py-1 text-xs font-semibold border ${form.empresaId === a.empresaId ? "bg-primary text-white border-primary" : "bg-white text-primary border-primary/40 hover:bg-primary/5"}`}>
                {a.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className={labelCls}>Nome da Empresa *</label>
        <EmpresaAutocomplete
          value={form.empresa}
          onChange={(nome, id) => setForm({ ...form, empresa: nome, empresaId: id })}
          inputCls={inputCls}
        />
      </div>

      <div className="mb-4">
        <label className={labelCls}>Título do relato *</label>
        <input className={inputCls} aria-label="Título do relato" value={form.titulo} onChange={set("titulo")} placeholder="Ex: Não deram retorno após entrevista" required />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="mb-4">
          <label className={labelCls}>Descrição da vaga</label>
          <input className={inputCls} aria-label="Descrição da vaga" value={form.vaga} onChange={set("vaga")} placeholder="Ex: Desenvolvedor RPA Sênior" />
        </div>
        <div className="mb-4">
          <label className={labelCls}>Data do processo</label>
          <input type="date" max={hojeISO} aria-label="Data do processo" className={inputCls} value={form.dataProcesso} onChange={set("dataProcesso")} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="mb-4">
          <label className={labelCls}>Etapa que chegou</label>
          <select className={inputCls} aria-label="Etapa que chegou" value={form.etapa} onChange={set("etapa")}>
            {ETAPAS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
        <div className="mb-4">
          <label className={labelCls}>Tipo de relato</label>
          <select className={inputCls} aria-label="Tipo de relato" value={form.tipo} onChange={set("tipo")}>
            {TIPOS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className={labelCls}>Dias até a empresa retornar</label>
        <input type="number" min="0" aria-label="Dias até a empresa retornar" className={inputCls} value={form.diasParaRetorno} onChange={set("diasParaRetorno")} placeholder="Ex: 15 (deixe vazio se sumiram)" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="mb-4">
          <label className={labelCls}>Dificuldade do processo</label>
          <select className={inputCls} aria-label="Dificuldade do processo" value={form.dificuldade} onChange={set("dificuldade")}>
            <option value="">Não informar</option>
            <option value="1">1 — Muito fácil</option>
            <option value="2">2 — Fácil</option>
            <option value="3">3 — Médio</option>
            <option value="4">4 — Difícil</option>
            <option value="5">5 — Muito difícil</option>
          </select>
        </div>
        <div className="mb-4">
          <label className={labelCls}>Desfecho</label>
          <select className={inputCls} aria-label="Desfecho do processo" value={form.desfecho} onChange={set("desfecho")}>
            {DESFECHOS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className={labelCls}>Que tipo de retorno você recebeu da empresa?</label>
        <select className={inputCls} aria-label="Tipo de retorno recebido da empresa" value={form.tipoRetorno} onChange={set("tipoRetorno")}>
          {TIPOS_RETORNO.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-1">Ajuda a sinalizar empresas que somem ou só mandam resposta automática.</p>
      </div>

      <div className="mb-4">
        <label className={labelCls}>Perguntas / etapas da entrevista (ajuda o próximo candidato)</label>
        <textarea className={inputCls} aria-label="Perguntas / etapas da entrevista" rows="2" value={form.perguntas} onChange={set("perguntas")}
          placeholder="Ex: pediram um case de SQL, perguntaram sobre system design..." />
      </div>

      {/* Honeypot anti-spam: escondido de humanos; bots tendem a preencher */}
      <input
        type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={set("website")}
        name="website" aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />

      <div className="mb-4">
        <label className={labelCls}>
          Prazo que a empresa prometeu para retorno (dias)
        </label>
        <input
          type="number" min="1" max="120"
          aria-label="Prazo prometido para retorno (dias)"
          className={inputCls}
          value={form.prazoRespostaDias}
          onChange={set("prazoRespostaDias")}
        />
        <p className="text-xs text-gray-500 mt-1">
          Perto do fim do prazo, o sistema vai te perguntar se a empresa deu retorno.
        </p>
      </div>

      {!user && (
        <div className="mb-4">
          <label className={labelCls}>Seu nome (ou deixe em branco para anônimo)</label>
          <input className={inputCls} value={form.autorNome} onChange={set("autorNome")} placeholder="Anônimo" />
        </div>
      )}

      <div className="mb-4">
        <label className={labelCls}>Descrição detalhada *</label>
        <textarea className={inputCls} aria-label="Descrição detalhada" rows="4" value={form.descricao} onChange={set("descricao")} required />
      </div>

      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
        <label className="flex items-start gap-2 text-sm font-semibold text-gray-800">
          <input type="checkbox" checked={form.intermediar}
            onChange={(e) => { const on = e.target.checked; setForm(on ? { ...form, intermediar: true } : { ...form, intermediar: false, contatoNome: "", contatoEmail: "", contatoWhatsapp: "", cienteContato: false }); }}
            className="mt-0.5" />
          <span>🤝 Quero que o eaiRH convide a empresa a responder</span>
        </label>
        <p className="text-xs text-gray-500 ml-6">Opcional. Seu relato já fica público e conta para a reputação da empresa — e a sua identidade fica protegida. 💚</p>
        {form.intermediar && (
        <div className="space-y-2 border-t border-gray-200 pt-3">
        <label className={labelCls}>🔒 Pessoa de contato no processo (privado)</label>
        <p className="text-xs text-gray-500">
          Quem te atendeu/recrutou? O contato <strong>nunca aparece publicamente</strong> — usamos só para <strong>convidar a empresa a responder</strong> ao seu relato (com opção de descadastro). O WhatsApp fica guardado para uma fase futura.
        </p>
        <input className={inputCls} aria-label="Nome do recrutador (privado)" placeholder="Nome do recrutador" value={form.contatoNome} onChange={set("contatoNome")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input type="email" className={inputCls} aria-label="E-mail do recrutador (privado, opcional)" placeholder="E-mail do recrutador (opcional)" value={form.contatoEmail}
            onChange={(e) => { set("contatoEmail")(e); campoEmail.aoDigitar(); }} onBlur={campoEmail.aoSair} />
          <AvisoEmail {...campoEmail.aviso} />
          <input className={inputCls} aria-label="WhatsApp do recrutador (privado, opcional)" placeholder="WhatsApp (opcional)" value={form.contatoWhatsapp} onChange={set("contatoWhatsapp")} />
        </div>
        <p className="text-[11px] text-gray-500">ℹ️ Informe ao menos o <strong>e-mail</strong> do recrutador/empresa para enviarmos o convite (o WhatsApp será usado em uma fase futura).</p>
        {form.contatoEmail.trim() && (
          <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 p-3 text-[11px] leading-relaxed text-gray-700 space-y-2">
            <p className="font-bold text-amber-800 text-xs">📄 Termo de ciência — envio do convite (LGPD)</p>
            <p>Ao informar o contato de um recrutador/empresa, você concorda que:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>O eaiRH usará o e-mail <strong>somente</strong> para convidar a empresa a responder a este relato — nunca para outra finalidade nem para venda/compartilhamento.</li>
              <li>O contato <strong>não será exibido publicamente</strong> e fica acessível apenas à moderação.</li>
              <li>O destinatário pode <strong>se descadastrar</strong> a qualquer momento (link de opt-out em todo e-mail), conforme a LGPD.</li>
              <li>Você declara ter <strong>participado deste processo seletivo</strong> e que os dados são <strong>verdadeiros</strong>, assumindo a responsabilidade por eles (Lei 13.709/2018 — LGPD).</li>
            </ul>
            <label className="flex items-start gap-2 pt-1 text-gray-800">
              <input type="checkbox" checked={form.cienteContato} onChange={(e) => setForm({ ...form, cienteContato: e.target.checked })} className="mt-0.5" />
              <span><strong>Li e estou ciente.</strong> Autorizo o eaiRH a enviar o convite por e-mail ao contato informado. Veja a <Link to="/privacidade" target="_blank" className="text-primary underline">Política de Privacidade</Link>.</span>
            </label>
          </div>
        )}
        </div>
        )}
      </div>

      <div className="mb-4">
        <label className={labelCls}>📎 Anexar documentos/prints</label>
        <input
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,image/*,application/pdf"
          onChange={onPickFiles}
          className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:font-semibold file:text-gray-700 hover:file:bg-gray-200"
        />
        {anexos.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-gray-600">
            {anexos.map((f, i) => (
              <li key={i}>• {f.name} ({(f.size / 1024).toFixed(0)} KB)</li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Imagens ou PDF, até 5 MB cada (máx. {MAX_ANEXOS}). Os anexos são{" "}
          <strong>privados</strong> — usados apenas para moderação e direito de resposta da empresa.
        </p>
      </div>

      {/* Última decisão de privacidade antes de publicar — fica em destaque logo acima do botão. */}
      <label className="mb-4 flex items-start gap-3 rounded-xl border-2 border-primary/40 bg-primary/5 p-3 cursor-pointer">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.anonimo} onChange={(e) => setForm({ ...form, anonimo: e.target.checked })} aria-label="Publicar como anônimo" />
        <span className="text-sm text-gray-700">
          🕶️ <b>Publicar como anônimo</b> — seu nome não aparece no relato.
        </span>
      </label>

      {/* Escolha do candidato: a EMPRESA citada pode ver o nome (nunca o público). Sem isso o RH não localiza
          o processo e não consegue se posicionar — por isso vem marcado, mas a decisão é de quem relata. */}
      <label className="mb-4 flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={form.mostrarNomeParaEmpresa}
          onChange={(e) => setForm({ ...form, mostrarNomeParaEmpresa: e.target.checked })}
          aria-label="Permitir que a empresa citada veja meu nome"
        />
        <span className="text-sm text-gray-700">
          🏢 <b>Deixar a empresa ver o meu nome</b>
          <span className="mt-1 block text-xs text-gray-600">
            Só a empresa citada, nunca o público — no site o seu relato continua anônimo, e seu <b>e-mail e telefone
            seguem ocultos</b>. É o que permite ao RH localizar o seu processo internamente e responder de verdade,
            em vez de dizer que não achou. Se preferir, desmarque: aí nem a empresa saberá quem é.
          </span>
        </span>
      </label>

      <button
        type="submit"
        disabled={enviando}
        className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold py-2.5 px-4 rounded-lg"
      >
        {enviando ? "Enviando..." : user ? "Publicar relato" : "Continuar e publicar"}
      </button>

      <p className="text-xs text-gray-500 italic mt-3">
        As informações são de responsabilidade do usuário.
      </p>
    </form>
  );
}

export default ComplaintForm;
